import { Dayjs } from 'dayjs';
import type { DataObjectGroupsWithDescriptionsMapById } from '../../dataObjectGroup';
import { ActivePeriod, Frequency } from '../types';
import {
  getNextPeriodEnd,
  getPeriod,
  getPrevPeriodEnd,
} from '../../common/components';
import {
  getDayjs,
  getDayjsNow,
  getFromMapIfExists,
  MAX_TIMESTAMP,
  MIN_TIMESTAMP,
} from '../../common/utils';

export interface ValidPeriodOptions {
  frequency?: Frequency;
  isDisabledDate?: (date: Dayjs) => boolean;
  validRanges?: Array<[Dayjs | null, Dayjs | null]>;
  userFollowedGroupPeriodSkipCount?: number;
}

// {groupId}-{descriptionId} or {groupId}
export type GroupDescriptionRef = `${number}-${number}` | `${number}`;
export type GroupDescriptionRefObject = {
  groupId: number;
  descriptionId?: number;
};

export function getGroupDescriptionRef(
  groupId: number,
  descriptionId?: number | null,
): GroupDescriptionRef | undefined {
  if (descriptionId) {
    return `${groupId}-${descriptionId}`;
  }
  if (groupId) {
    return `${groupId}`;
  }
}

export function getGroupDescriptionType(
  groupDescriptionRef: GroupDescriptionRef,
) {
  const { descriptionId } = parseGroupDescriptionRef(groupDescriptionRef);
  return descriptionId ? 'description' : 'group';
}

export function parseGroupDescriptionRef(
  groupDescriptionRef: GroupDescriptionRef,
): GroupDescriptionRefObject {
  if (typeof groupDescriptionRef === 'number') {
    return { groupId: Number(groupDescriptionRef) };
  }

  if (
    typeof groupDescriptionRef === 'string' &&
    !groupDescriptionRef.includes('-')
  ) {
    return { groupId: Number(groupDescriptionRef) };
  }

  const [groupId, descriptionId] = groupDescriptionRef.split('-');
  const parsedGroupId = groupId ? Number(groupId) : undefined;
  if (parsedGroupId === undefined || isNaN(parsedGroupId)) {
    throw new Error(`Invalid groupId in groupDescriptionRef: ${groupId}`);
  }

  const parsedDescriptionId = descriptionId ? Number(descriptionId) : undefined;

  return {
    groupId: parsedGroupId,
    descriptionId: parsedDescriptionId,
  };
}

/**
 * Determines whether a given date falls within a specified period (inclusive []).
 * @param valueDate - The date to check.
 * @param period - A tuple containing the start and end dates of the period, or `null` for open-ended.
 * @returns `true` if `valueDate` is within the specified period; otherwise, `false`.
 */
export function isWithinPeriod(
  valueDate: Dayjs,
  period: [Dayjs | null, Dayjs | null],
) {
  const [validFrom, validTo] = period;

  if (validFrom && validTo) {
    return valueDate.isBetween(validFrom, validTo, 'day', '[]');
  }

  if (validFrom) {
    return !valueDate.isBefore(validFrom);
  }

  if (validTo) {
    return !valueDate.isAfter(validTo);
  }

  return true;
}

export function isDisabledDate(
  valueDate: Dayjs,
  validRanges?: Array<[Dayjs | null, Dayjs | null]>,
) {
  return (
    validRanges?.every((range) => {
      if (range[0] && range[1]) {
        return !valueDate.isBetween(range[0], range[1], 'day', '[]');
      }
      if (range[0]) {
        return valueDate.isBefore(range[0]);
      }
      if (range[1]) {
        return valueDate.isAfter(range[1]);
      }

      return true;
    }) ?? true
  );
}

/**
 * Computes the value date for a given selected date, frequency, and valid ranges.
 * If the selected date does not conform to the valid ranges, it finds the closest valid value date.
 */
export function valueDate(
  selectedDate: Dayjs | null,
  frequency?: Frequency,
  validRanges?: Array<[Dayjs | null, Dayjs | null]>,
  userFollowedGroupPeriodSkipCount?: number,
): Dayjs | null {
  const date = selectedDate
    ? getPeriod(selectedDate, frequency, userFollowedGroupPeriodSkipCount)[1]
    : getPrevPeriodEnd(
        getDayjsNow(),
        frequency,
        userFollowedGroupPeriodSkipCount,
      );

  if (!date || !isDisabledDate(date, validRanges)) {
    return date;
  }

  if (!validRanges) {
    return null;
  }

  // Selected date does not fit into the valid ranges.
  // Find the first date suitable in active ranges.
  for (const range of validRanges) {
    const resolvedRange = resolveValidRange(range);
    const [from] = resolvedRange;

    // Date is before range start: find first valid period in this range.
    if (date.isBefore(from, 'day')) {
      const firstPeriodStart = findFirstPeriodStartInRange(
        resolvedRange,
        frequency,
        userFollowedGroupPeriodSkipCount,
      );
      if (firstPeriodStart) return firstPeriodStart;
      continue;
    }

    // Date is after range start: find last valid period in this range.
    const lastPeriodEnd = findLastPeriodEndInRange(
      resolvedRange,
      frequency,
      userFollowedGroupPeriodSkipCount,
    );
    if (lastPeriodEnd) return lastPeriodEnd;
  }

  return null;
}

const resolveValidRange = ([from, to]: [Dayjs | null, Dayjs | null]): [
  Dayjs,
  Dayjs,
] => [
  getDayjs(from) || getDayjs(MIN_TIMESTAMP)!,
  getDayjs(to) || getDayjs(MAX_TIMESTAMP)!,
];

const isDateWithinRangeByDay = (date: Dayjs, [from, to]: [Dayjs, Dayjs]) =>
  !date.isBefore(from, 'day') && !date.isAfter(to, 'day');

/**
 * Find the first period-start date in a range whose period-end is within bounds.
 * Walks forward by period boundaries instead of days.
 */
function findFirstPeriodStartInRange(
  range: [Dayjs, Dayjs],
  frequency?: Frequency,
  userFollowedGroupPeriodSkipCount?: number,
): Dayjs | null {
  const [from, to] = range;
  let periodEndValueDate = getPeriod(
    from,
    frequency,
    userFollowedGroupPeriodSkipCount,
  )[1];

  // Advance by period until we reach the range boundary.
  while (periodEndValueDate && periodEndValueDate.isBefore(from, 'day')) {
    periodEndValueDate = getNextPeriodEnd(periodEndValueDate, frequency);
  }

  // Check if this period-end is still within the range.
  if (!periodEndValueDate || periodEndValueDate.isAfter(to, 'day')) {
    return null;
  }

  return getPeriod(
    periodEndValueDate,
    frequency,
    userFollowedGroupPeriodSkipCount,
  )[0];
}

/**
 * Find the last period-end date in a range.
 * Walks backward until the period-end is within bounds.
 */
function findLastPeriodEndInRange(
  range: [Dayjs, Dayjs],
  frequency?: Frequency,
  userFollowedGroupPeriodSkipCount?: number,
): Dayjs | null {
  const [_from, to] = range;
  let periodEndValueDate = getPeriod(
    to,
    frequency,
    userFollowedGroupPeriodSkipCount,
  )[1];

  // Walk backward by period until we reach the range boundary.
  while (periodEndValueDate && periodEndValueDate.isAfter(to, 'day')) {
    periodEndValueDate = getPrevPeriodEnd(
      periodEndValueDate,
      frequency,
      userFollowedGroupPeriodSkipCount,
    );
  }

  // Verify the result is in range.
  if (
    !periodEndValueDate ||
    !isDateWithinRangeByDay(periodEndValueDate, range)
  ) {
    return null;
  }

  return periodEndValueDate;
}

export function updateFrequency(
  sampleType: 'group' | 'description',
  id: number,
  setPeriodOptions?: (periodOptions: ValidPeriodOptions) => void,
  groupsById?: DataObjectGroupsWithDescriptionsMapById,
  descriptionGroupId?: number | string,
) {
  let frequency: Frequency | undefined;
  let activePeriods: ActivePeriod[] | undefined;
  let userFollowedGroupPeriodSkipCount: number | undefined;
  if (sampleType === 'description') {
    const description =
      groupsById &&
      getFromMapIfExists(
        groupsById,
        descriptionGroupId || null,
      )?.descriptions.find((object) => object.id === id);

    frequency = description?.dataObjectGroupData?.frequency?.code as Frequency;
    activePeriods = description?.activePeriods ?? undefined;
    userFollowedGroupPeriodSkipCount =
      description?.userFollowedGroupPeriodSkipCount ?? undefined;
  } else {
    const group = groupsById && getFromMapIfExists(groupsById, id);

    frequency = group?.dataObjectGroupData?.frequency?.code as Frequency;
    activePeriods = group?.descriptions
      .map((description) => description.activePeriods)
      .filter((activePeriods) => !!activePeriods)
      .flat(1) as ActivePeriod[] | undefined;
    userFollowedGroupPeriodSkipCount =
      group?.userFollowedGroupPeriodSkipCount ?? undefined;
  }
  const validRanges = activePeriods?.map(
    (period) =>
      [
        getDayjs(period.validFrom) || null,
        getDayjs(period.validTo) || null,
      ] as [Dayjs | null, Dayjs | null],
  );

  setPeriodOptions?.({
    frequency,
    isDisabledDate: (valueDate: Dayjs) =>
      isDisabledDate(getPeriod(valueDate, frequency)[1], validRanges),
    validRanges,
    userFollowedGroupPeriodSkipCount,
  });

  return {
    frequency,
    validRanges,
    userFollowedGroupPeriodSkipCount,
  };
}
